import { FormControl, Grid, Link as MaterialLink } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import RenewalsApi from '../../api/RenewalsApi';
import { AuthData } from '../../context/auth';
import { PolicyData, withPolicyContext } from '../../context/policy';
import { PolicyCharacteristics } from '../../types/policy';
import {
	getCharacteristicAtTime_T_,
	getDateErrors,
	getOverrideResourcesAsOfTimestampAtTime,
	isPolicyEditable,
} from '../../util/util';
import { StyledSectionContainer } from '../common';
import { FastForm } from '../fastForm';
import {
	DATE_PRECISION,
	DATE_PRECISION_INPUT_FORMAT,
} from '../fastForm/data/constants';
import FieldLabel from '../fields/FieldLabel';
import DefaultDatePicker, {
	DefaultDatePickerValue,
} from '../form/DefaultDatePicker';
import ReactSelect from './fields/ReactSelect';

interface Props {
	authData: AuthData;
	policyData: PolicyData;
}

const useStyles = makeStyles((theme) => ({
	selectInput: {
		'& .MuiSvgIcon-root': {
			color: 'rgba(0, 0, 0, 0.54)',
		},
	},
}));

const PolicyDetails = (props: Props) => {
	const { authData, policyData } = props;
	const { hasPermission } = authData;

	const {
		selectedQuote,
		policyUpdateObj,
		setPolicyUpdateObj,
		quoteUpdateObj,
		setQuoteUpdateObj,
		productConfig,
		policy,
		policyViewTimestamp,
		fields: form,
		saveFastForm,
		setLoading,
		resourcesTimestamps,
	} = policyData;

	const classes = useStyles();

	const isInEditMode =
		authData.hasPermission('quote.update') &&
		isPolicyEditable(policy, selectedQuote);
	const updateObject = selectedQuote ? quoteUpdateObj : policyUpdateObj;
	const setUpdateObject = selectedQuote
		? setQuoteUpdateObj
		: setPolicyUpdateObj;

	if (!policy) {
		return null;
	}

	let characteristics = policy.characteristics.filter(
		(pc) => !pc.replacedTimestamp,
	)[0];

	if (selectedQuote) {
		characteristics = selectedQuote.characteristics;
	} else if (policy.issuedTimestamp) {
		characteristics = getCharacteristicAtTime_T_(
			policy.characteristics,
			parseInt(policyViewTimestamp, 10),
		) as PolicyCharacteristics;
	}

	const { policyStartTimestamp } = characteristics;

	const getPaymentScheduleOptions = () => {
		if (!productConfig) {
			return null;
		}

		const { paymentSchedules } = productConfig.policyConfiguration;

		return paymentSchedules.map((paymentSchedule) => ({
			value: paymentSchedule.name,
			label: paymentSchedule.displayName,
		}));
	};

	const paymentScheduleName = updateObject.paymentScheduleName
		? updateObject.paymentScheduleName
		: policy.issuedTimestamp
		? policy.paymentScheduleName
		: selectedQuote.paymentScheduleName;

	const paymentScheduleLabel =
		getPaymentScheduleOptions().find((ps) => ps.value === paymentScheduleName)
			?.label || paymentScheduleName;
	const startTimestamp =
		updateObject && 'policyStartTimestamp' in updateObject
			? updateObject.policyStartTimestamp
			: policyStartTimestamp;

	let endTimestamp;
	if (updateObject && 'policyEndTimestamp' in updateObject) {
		endTimestamp = updateObject.policyEndTimestamp;
	} else if (policy.issuedTimestamp) {
		// display the policy.effectiveContractEndTimestamp whenever the policy has been issued
		endTimestamp = policy.effectiveContractEndTimestamp;
	} else {
		endTimestamp = characteristics.endTimestamp;
	}

	const policyStartDate = new Date(parseInt(startTimestamp, 10));
	const policyEndDate = new Date(parseInt(endTimestamp, 10));

	const [policyStart, setPolicyStart] =
		// eslint-disable-next-line react-hooks/rules-of-hooks
		React.useState<DefaultDatePickerValue>(policyStartDate);
	const [policyEnd, setPolicyEnd] =
		// eslint-disable-next-line react-hooks/rules-of-hooks
		React.useState<DefaultDatePickerValue>(policyEndDate);

	// eslint-disable-next-line react-hooks/rules-of-hooks
	useEffect(() => {
		setPolicyStart(policyStartDate);
		setPolicyEnd(policyEndDate);
	}, [startTimestamp, endTimestamp]);

	// eslint-disable-next-line react-hooks/rules-of-hooks
	const { data: renewalsResponse } = useQuery({
		queryKey: ['renewals', policy.locator, authData.auth.authorizationToken],
		enabled: hasPermission('renewal.fetch'),
		staleTime: 1000 * 60 * 10, // 10 minutes
		queryFn: () =>
			RenewalsApi.getRenewalsForPolicy(
				authData.auth.authorizationToken,
				policy.locator,
			),
	});

	const getOverrideResourcesAsOfTimestampForCurrentView = ():
		| string
		| undefined => {
		if (policy.issuedTimestamp) {
			return getOverrideResourcesAsOfTimestampAtTime(
				policy,
				renewalsResponse?.data,
				parseInt(policyViewTimestamp, 10),
			);
		} else {
			if (updateObject.resetOverrideResourcesAsOfTimestamp) {
				return null;
			}
			if (updateObject.overrideResourcesAsOfTimestamp) {
				return updateObject.overrideResourcesAsOfTimestamp;
			} else {
				return policy.overrideResourcesAsOfTimestamp;
			}
		}
	};

	const overrideResourcesAsOfTimestamp =
		getOverrideResourcesAsOfTimestampForCurrentView();

	const policyStartErrors = getDateErrors(
		policyStart,
		null,
		null,
		true,
		authData,
	);
	const policyEndErrors = getDateErrors(
		policyEnd,
		policyStart as Date,
		null,
		true,
		authData,
	);

	const renderForm = () => {
		const options = getPaymentScheduleOptions();
		const updatePaymentSchedule = (option: {
			value: string;
			label: string;
		}) => {
			const paymentSchedule = option.value;
			updateObject.paymentScheduleName = paymentSchedule;
			setUpdateObject(updateObject);
		};

		const updateOverrideResourcesTimestamp = (option: {
			value: string;
			label: string;
		}) => {
			const overrideResourcesTimestamp = option.value;
			if (
				!overrideResourcesTimestamp &&
				policy.overrideResourcesAsOfTimestamp
			) {
				updateObject.resetOverrideResourcesAsOfTimestamp = true;
				updateObject.overrideResourcesAsOfTimestamp = null;
			} else {
				updateObject.overrideResourcesAsOfTimestamp =
					overrideResourcesTimestamp;
			}
			setUpdateObject(updateObject);
		};

		const updatePolicyStart = (date: DefaultDatePickerValue) => {
			const validDate = date && !isNaN(date.getTime());
			setPolicyStart(date);
			updateObject.policyStartTimestamp = validDate
				? `${(date as Date).getTime()}`
				: null;
			setUpdateObject(updateObject);
		};

		const updatePolicyEnd = (date: DefaultDatePickerValue) => {
			const validDate = date && !isNaN(date.getTime());
			setPolicyEnd(date);
			updateObject.policyEndTimestamp = validDate
				? `${(date as Date).getTime()}`
				: null;
			setUpdateObject(updateObject);
		};

		const shouldDisplayOverrideInput =
			isPolicyEditable(policy, selectedQuote) &&
			resourcesTimestamps?.length > 0;

		return (
			<section>
				<Grid container alignContent='center' spacing={2}>
					<Grid item xs={12} alignSelf='flex-end' sx={{ textAlign: 'right' }}>
						{authData.hasPermission('auxData.fetch') && (
							<MaterialLink component={Link} to='../auxData'>
								Aux Data
							</MaterialLink>
						)}
					</Grid>
					<Grid item xs={shouldDisplayOverrideInput ? 3 : 4}>
						<DefaultDatePicker
							id='policy-start'
							label='Policy Start'
							required
							disabled={!isInEditMode}
							showCalendar
							fullWidth
							style={{ width: '100%' }}
							error={!policyStartErrors.isValid}
							errorMessage={
								!policyStartErrors.isValid ? policyStartErrors.message : null
							}
							value={policyStart}
							onChange={(date) => updatePolicyStart(date)}
						/>
					</Grid>
					<Grid item xs={shouldDisplayOverrideInput ? 3 : 4}>
						<DefaultDatePicker
							id='policy-end'
							label='Policy End'
							required
							disabled={!isInEditMode}
							showCalendar
							fullWidth
							style={{ width: '100%' }}
							error={!policyEndErrors.isValid}
							errorMessage={
								!policyEndErrors.isValid ? policyEndErrors.message : null
							}
							minDate={policyStart}
							value={policyEnd}
							onChange={(date) => updatePolicyEnd(date)}
						/>
					</Grid>
					{shouldDisplayOverrideInput && (
						<Grid item xs={3}>
							<FormControl fullWidth>
								<FieldLabel title={'Override Resources Selection'} />
								<ReactSelect
									includeEmptyValue={true}
									id='override-resources-ts-selector'
									className={isInEditMode ? classes.selectInput : ''}
									value={{
										value: `${overrideResourcesAsOfTimestamp}`,
										label: overrideResourcesAsOfTimestamp
											? authData.getZonedFormattedDateString(
													overrideResourcesAsOfTimestamp,
													DATE_PRECISION_INPUT_FORMAT[DATE_PRECISION.MINUTE],
											  )
											: null,
									}}
									options={resourcesTimestamps.map((ts) => ({
										value: `${ts}`,
										label: authData.getZonedFormattedDateString(
											`${ts}`,
											DATE_PRECISION_INPUT_FORMAT[DATE_PRECISION.MINUTE],
										),
									}))}
									onChange={updateOverrideResourcesTimestamp}
									isDisabled={!isInEditMode}
								/>
							</FormControl>
						</Grid>
					)}
					<Grid item xs={shouldDisplayOverrideInput ? 3 : 4}>
						{options.length > 0 && (
							<FormControl fullWidth>
								<FieldLabel required title={'Payment Schedule'} />
								<ReactSelect
									id='payment-schedule-selector'
									className={isInEditMode ? classes.selectInput : ''}
									value={{
										value: paymentScheduleName,
										label: paymentScheduleLabel,
									}}
									options={options.map((v) => ({
										value: v.value,
										label: v.label,
									}))}
									onChange={updatePaymentSchedule}
									isDisabled={!isInEditMode}
								/>
							</FormControl>
						)}
					</Grid>
					<FastForm
						formName='policyForm'
						form={form}
						configVersion={props.policyData.productConfig.configVersion}
						authData={props.authData}
						productName={props.policyData.productConfig.name}
						disabled={!isInEditMode}
						save={saveFastForm}
						setLoading={setLoading}
						effectiveDate={overrideResourcesAsOfTimestamp}
					/>
				</Grid>
			</section>
		);
	};

	return <StyledSectionContainer>{renderForm()}</StyledSectionContainer>;
};

export default withPolicyContext<Props>(PolicyDetails);
